/**
 * Formatting and status vocabulary.
 *
 * One rule runs through all of it: a value we do not have is drawn as unknown,
 * never as 0 and never as an em dash standing in for success.  `unknown` is its
 * own tone with its own glyph and its own hatch, and nothing folds it into a
 * neighbour.
 */

import type { Format } from "../panel/spec";

/**
 * The six tones.
 *
 * They live here and not in the panel contract because they are not in it: a
 * plugin declares a column's `format`, never its colour. Which state is good
 * and which is alarming is this shell's reading of the ledger vocabulary, and
 * it is the shell that has to answer for it.
 */
export const TONES = ["neutral", "good", "warning", "critical", "unknown", "accent"] as const;
export type Tone = (typeof TONES)[number];

export const TONE_GLYPH: Record<Tone, string> = {
  good: "●",
  warning: "▲",
  critical: "■",
  unknown: "◇",
  neutral: "○",
  accent: "◆",
};

/**
 * The ledger state vocabularies of CHARTER §1.6, mapped to the six tones.
 *
 * `replied` and `silent` left with the outbox (§7.139 ③): the difference
 * between them was whether the core had built a delivery row, and with no
 * delivery ledger the core has no way to know whether anything was said and no
 * business knowing. A turn that ran to its end is `completed`, whether a reply
 * plugin spoke or nothing did.
 *
 * `sending` / `sent` / `failed` are this console's own words for a reply
 * bubble — not a ledger state, and not written anywhere: they say whether the
 * `workbench.reply` call that drew the bubble has come back yet.
 *
 * `released` is neutral on purpose (§7.146 ③): the hold ended and the call was
 * re-issued on a row of its own, so this row is finished without an outcome of
 * its own. Drawing it green would claim a success that happened elsewhere, and
 * drawing it as still pending is what the old `prepared` did wrong.
 */
const STATE_TONE: Record<string, Tone> = {
  received: "neutral",
  in_turn: "neutral",
  completed: "good",
  stopped: "neutral",
  failed: "critical",
  sending: "neutral",
  sent: "good",
  prepared: "neutral",
  held: "warning",
  released: "neutral",
  dispatching: "neutral",
  succeeded: "good",
  reserved: "neutral",
  settled: "good",
  unknown: "unknown",
};

const STATE_LABEL: Record<string, string> = {
  received: "已收到",
  in_turn: "在轮里",
  completed: "已办完",
  stopped: "已停止",
  failed: "失败",
  sending: "发送中",
  sent: "已送出",
  prepared: "已备好",
  held: "等你批",
  released: "挂起结束",
  dispatching: "执行中",
  succeeded: "已完成",
  reserved: "已预留",
  settled: "已结算",
  unknown: "未知",
};

export function toneForState(state: unknown): Tone {
  if (typeof state !== "string" || state === "") return "unknown";
  return STATE_TONE[state] ?? "neutral";
}

export function labelForState(state: unknown): string {
  if (typeof state !== "string" || state === "") return "未知";
  return STATE_LABEL[state] ?? state;
}

/**
 * Where a claimed call stands (`panel/spec.ts` `RECORD_STATES`), as a tone.
 *
 * A tone and no word: what a record's card says is the claiming plugin's own
 * sentence (`PanelSpec.states`), and all this shell answers for is how the
 * state reads at a glance. `stopped` is neutral, like every other stop in this
 * file -- somebody ended it on purpose, which is neither an answer nor a
 * breakage. `running` and `working` are drawn moving rather than with a glyph
 * (`panel/records.tsx`).
 */
const RECORD_TONE: Record<string, Tone> = {
  running: "neutral",
  working: "neutral",
  stopping: "neutral",
  done: "good",
  failed: "critical",
  stopped: "neutral",
  unknown: "unknown",
};

export function recordTone(state: unknown): Tone {
  return RECORD_TONE[String(state)] ?? "unknown";
}

const LEDGER_LABEL: Record<string, string> = {
  obligations: "欠条",
  usage: "用量",
  effects: "效果",
  l0_events: "L0",
  registry: "注册史",
};

export function ledgerLabel(name: string): string {
  return LEDGER_LABEL[name] ?? name;
}

/**
 * One cell, in the five formats `PanelColumn.format` has.
 *
 * Five, not the ten this shell used to offer: money, bytes, duration and the
 * rest are not in `pa.contracts.registries`, so no plugin could ever ask for
 * them. A format the contract cannot name is a format nobody can use.
 */
export function formatValue(value: unknown, format: Format = "text"): string {
  if (value === null || value === undefined || value === "") return "未知";
  switch (format) {
    case "number":
      return typeof value === "number" ? value.toLocaleString("zh-CN") : String(value);
    case "time":
      return formatTime(value);
    case "status":
      return labelForState(value);
    case "ref":
      return String(value);
    default:
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
  }
}

/**
 * Contract timestamps are float seconds (`pa.contracts.ids.Timestamp = float`).
 *
 * Strings are accepted too, but only ISO ones: that is the shape a person
 * hand-writes into a fixture or pastes in from elsewhere.  Reading seconds as
 * milliseconds draws 2026 as 1970, so this decides by magnitude rather than
 * by guessing.
 */
export function parseTime(value: unknown): Date | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    if (value <= 0) return null;
    // Below 1e12 means seconds: every millisecond stamp after 2001 is larger,
    // and seconds do not cross it until the year 33658, so neither side is near.
    return new Date(value < 1e12 ? value * 1000 : value);
  }
  if (typeof value !== "string" || value === "") return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatTime(value: unknown): string {
  const date = parseTime(value);
  if (!date) return "未知";
  return date.toLocaleString("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

/** A message's time in the conversation: to the minute, as a person reads a clock. */
export function formatClock(value: unknown): string {
  const date = parseTime(value);
  if (!date) return "未知";
  return date.toLocaleTimeString("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** A one-line summary of a ledger row for the timeline, never a raw JSON dump. */
export function summarizeRow(row: Record<string, unknown>): string {
  // `key` is the registry entry a write changed -- `producer_video.runs`, not
  // the generation id underneath it.  Without it every registry line on the
  // timeline summarised itself as `gen-37`, which says when but never what.
  const named = ["text", "title", "tool", "summary", "model", "display_name", "reason", "key"];
  for (const key of named) {
    const value = row[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  const id = row.id;
  return typeof id === "string" ? id : "";
}

/**
 * Chinese names for L0 events.  The keys are the core's own words
 * (`pa.contracts.events.L0Kind`, carrying the `l0.` prefix on the tail), not a
 * vocabulary invented here.
 */
const KIND_LABEL: Record<string, string> = {
  "l0.inbound": "收到消息",
  "l0.ingress_refused": "门口拒了一条",
  "l0.model_request": "组装并出网",
  "l0.model_response": "模型回了",
  "l0.tool_call": "要调工具",
  "l0.tool_result": "工具结果",
  "l0.effect.held": "有人先挂住了它",
  "l0.effect.released": "挂起结束了",
  "l0.turn_final": "一轮结束",
  "l0.child_spawned": "开了子会话",
  "l0.obligation": "欠条变了状态",
  // §7.143's three new kinds.  `usage` and `grant` used to be tables the core
  // wrote and never read back; a registry write was already an event and now
  // has company.
  "l0.usage": "这次调用的账",
  "l0.grant": "给了插件哪些权",
  // Two writes the record keeps by name only: one row per write, never the
  // value (ruling p1-hub for secrets, §7.204 for ordinary settings -- which
  // the owner's settings page now writes directly, so this row is the whole
  // of what the record says about that press).
  "l0.secret_set": "写了一把密钥",
  "l0.config_set": "改了设置",
};

/** A registry write is an `l0.registry` event, handed out as `registry/<kind>`. */
const REGISTRY_LABEL: Record<string, string> = {
  channels: "渠道",
  tools: "工具",
  skills: "技能",
  models: "模型",
  ui_panels: "面板",
  // Not one of the five, and on the tail all the same.  `plugin` is the load
  // record the host writes whether the plugin registered anything or not, and
  // `identities` is an open kind a plugin declared (§7.140) -- the registry
  // takes any kind, so this map is a courtesy and never a whitelist.
  plugin: "插件",
  identities: "身份",
};

/**
 * The name of one event.
 *
 * Every kind on the tail is an L0 kind or a `registry/<kind>` write, and since
 * §7.143 that is the whole list: the row ledgers used to ride the tail with
 * their table name as the kind and the verb in the row's own status column, so
 * a badge was assembled from kind *and* row. Those rows are rebuilt from the
 * events now (`workbench/server/derived.py`) and never arrive as events, so
 * there is nothing left for a second argument to add.
 */
export function kindLabel(kind: string): string {
  if (KIND_LABEL[kind]) return KIND_LABEL[kind];
  if (kind.startsWith("registry/")) {
    const registry = kind.slice("registry/".length);
    return `注册·${REGISTRY_LABEL[registry] ?? registry}`;
  }
  // Unrecognised: hand back the machine name as it is.  Inventing a Chinese
  // word for it would be worse than showing the machine name.
  return kind;
}
